import { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { JourneyShell } from "@/components/journey/JourneyShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { useJourneyContent } from "@/hooks/useJourneyContent";
import { getAccount, getAssessments, reportDownloadUrl, requestInvoice } from "@/lib/journeyApi";
import "@/pages/Journey.css";

export default function Unlock() {
  const { user } = useAuth();
  const { ui, pricing, loading } = useJourneyContent({ pricing: true });
  const [assessment, setAssessment] = useState(null);
  const [order, setOrder] = useState(null);
  const [paidReport, setPaidReport] = useState(null);

  const refreshStatus = useCallback(async () => {
    const [assessments, account] = await Promise.all([getAssessments(), getAccount()]);
    const activeOrder = account.orders.find((item) => item.state === "invoice_requested")
      || account.orders.find((item) => item.state === "paid")
      || null;
    const selected = activeOrder
      ? assessments.find((item) => item.assessment_id === activeOrder.assessment_id)
      : assessments.find((item) => ["paid_pending", "paid"].includes(item.tier));
    const report = activeOrder
      ? account.reports.find((item) => item.assessment_id === activeOrder.assessment_id && item.kind === "paid")
      : account.reports.find((item) => item.kind === "paid");
    setAssessment(selected || (report ? { assessment_id: report.assessment_id, tier: "paid" } : null));
    setOrder(activeOrder);
    setPaidReport(report || null);
  }, []);

  useEffect(() => {
    let active = true;
    refreshStatus().catch(() => {
      if (active) setAssessment(null);
    });
    return () => { active = false; };
  }, [refreshStatus]);

  useEffect(() => {
    if (!order || order.state === "paid") return undefined;
    const timer = window.setInterval(() => refreshStatus(), 15000);
    return () => window.clearInterval(timer);
  }, [order, refreshStatus]);

  const region = useMemo(() => pricing?.regions?.[user?.country] || pricing?.regions?.OTHER, [pricing, user?.country]);
  if (loading || !ui || !pricing || !assessment || !region) return <div className="journey-loading" data-testid="unlock-loading-state" />;
  const price = ui.unlock.price_line.replace("{symbol}", region.symbol).replace("{amount}", region.amount).replace("{currency}", region.currency);

  const request = async () => {
    setOrder(await requestInvoice(assessment.assessment_id));
    await refreshStatus();
  };

  const requestedBody = order
    ? ui.unlock.requested_body
        .replace("{email}", user.email)
        .replace("{promise}", pricing.invoice_response_promise)
        .replace("{invoice_ref}", order.ref)
    : "";

  return (
    <JourneyShell>
      <section className="journey-panel journey-panel--narrow" data-testid="unlock-panel">
        <h1 data-testid="unlock-title">{paidReport ? "Your full report is ready" : order ? ui.unlock.requested_title : ui.unlock.title}</h1>
        {!order && <p data-testid="unlock-body">{ui.unlock.body}</p>}
        <div className="journey-price" data-testid="unlock-price">{price}</div>
        {paidReport ? (
          <>
            <a className="journey-primary journey-download" href={reportDownloadUrl(paidReport.report_id)} data-testid="paid-report-download-link">
              <Download aria-hidden="true" /> Download full report
            </a>
            <div className="journey-next-actions" data-testid="final-completion-actions">
              <Link className="journey-secondary" to="/supplement-check" data-testid="final-supplement-plan-link">Get my supplement plan</Link>
              <Link className="journey-secondary" to="/doctor-request" data-testid="final-doctor-match-link">Request a doctor match</Link>
            </div>
          </>
        ) : order ? (
          <>
            <p data-testid="invoice-next-steps">{requestedBody}</p>
            <p className="journey-reference" data-testid="invoice-reference">{order.ref}</p>
          </>
        ) : (
          <Button onClick={request} className="journey-primary" data-testid="request-invoice-button">{ui.unlock.button}</Button>
        )}
        {order && (
          <div className="journey-next-actions" data-testid="invoice-next-actions">
            <Link className="journey-primary" to="/account" data-testid="invoice-history-link">Go to my history</Link>
            <Link to="/" data-testid="invoice-home-link">Return home</Link>
          </div>
        )}
        {order && !paidReport && (
          <aside className="journey-explore" data-testid="invoice-questions-invitation">
            <BookOpen aria-hidden="true" />
            <div>
              <p>The perimenopause community is asking real questions.</p>
              <a href="/questions" data-testid="invoice-questions-link">Explore the questions bank</a>
            </div>
          </aside>
        )}
      </section>
    </JourneyShell>
  );
}